import { ActionContextType, ActionsContext, MxAction } from '../../Shell/Pandora/model/action';

import type { IComponentController } from 'angular';
import template from './dashboard-control-panel.component.html';
import { I18n } from '../../i18n/mx-internationalization';
import { DashboardPageComponent, DashboardPageController } from '../dashboard-page/dashboard-page.component';
import type { MxActionManagerService } from '../../Shell/Pandora/services/mx-action-manager-service';
import type { DashboardService } from '../dashboard.service';
import type { NotificationService } from '../../App/services/mx-notification-service';
import type { DataModelCacheService } from '../../Shell/PandoraSolutionBuilderAgent/services/data-model-cache.service';
import type { ConfigService } from '../../App/services/mx-config-service';

export class DashboardControlPanelController implements IComponentController {
	page!: DashboardPageController;
	actions: MxAction[] = [];

	static $inject = [
		'mx.internationalization',
		'mx.shell.ActionManagerService',
		'mx.sb.Dashboard.Service',
		'mx.shell.NotificationService',
		'mx.SolutionBuilderAgent.DataModelCacheService',
		'mx.shell.Config',
	];

	constructor(
		private readonly i18n: I18n,
		private readonly actionManagerService: MxActionManagerService,
		private readonly dashboardService: DashboardService,
		private readonly notificationService: NotificationService,
		private readonly dataModelCacheService: DataModelCacheService,
		private readonly configService: ConfigService
	) {}

	$onInit() {
		this.getActions().then((actions) => (this.actions = actions));
	}

	$onDestroy() {
		this.dataModelCacheService.enable();
	}

	private async initActions() {
		this.actions = await this.getActions();
	}

	async onSaveClick() {
		await this.page.disableConfigurationMode();
		await this.initActions();
		this.dataModelCacheService.enable();
	}

	private async showConfirmation(confirmationTitle: string, confirmationMessage: string): Promise<boolean> {
		try {
			await this.notificationService.confirm(confirmationTitle, confirmationMessage);
			return true;
		} catch (e) {
			return false;
		}
	}

	async getActions() {
		const dashboardActions: MxAction[] = [];

		const actionContext = new ActionsContext([], []);
		const actions = this.actionManagerService.filterActions(await this.actionManagerService.getActions(actionContext, [ActionContextType.None]));
		const switchBackOriginal = actions.find((action) => action.name.startsWith('UUX.Client.Dashboard.SwitchBack'));
		const publishActionOriginal = actions.find((action) => action.name.startsWith('UUX.Client.Dashboard.PublishLayout'));
		const removeCustomizationActionOriginal = actions.find((action) => action.name.startsWith('UUX.Client.Dashboard.RevertPublishedLayout'));
		const removePersonalizationActionOriginal = actions.find((action) => action.name.startsWith('UUX.Client.Dashboard.RemovePersonalization'));
		const forceReloadActionOriginal = actions.find((action) => action.name.startsWith('UUX.Client.Dashboard.ForceReload'));

		const addAction = (originalAction: MxAction, exec: () => void, popover?: string, onPopoverClose?: () => void) => {
			const execute = async () => {
				const isConfirmed =
					!originalAction.serviceOperation?.ConfirmationMessage ||
					(await this.showConfirmation(originalAction.title, (originalAction.serviceOperation?.ConfirmationMessage || '') as string));

				if (isConfirmed) {
					exec();
				}
			};

			const action = this.createAction({
				name: originalAction.name,
				title: originalAction.title,
				icon: originalAction.icon,
				execute,
				popover: popover,
				onPopoverClose: onPopoverClose,
			});

			dashboardActions.push(action);
		};

		if (switchBackOriginal) {
			addAction(switchBackOriginal, () => this.dashboardService.dashboardLookOperation(this.page.dashboard.navigationId));
		}

		if (publishActionOriginal && this.dashboardService.hasPersonalization(this.page.dashboard)) {
			addAction(publishActionOriginal, async () => {
				await this.dashboardService.publishDashboard(this.page.dashboard);
				this.page.reloadPage();
			});
		}

		if (removeCustomizationActionOriginal && this.page.dashboard.isCustomized) {
			addAction(removeCustomizationActionOriginal, async () => {
				await this.dashboardService.removeCustomization(this.page.dashboard);
				this.page.reloadPage();
			});
		}

		if (removePersonalizationActionOriginal && this.dashboardService.hasPersonalization(this.page.dashboard)) {
			addAction(removePersonalizationActionOriginal, async () => {
				await this.dashboardService.removePersonalization(this.page.dashboard);
				this.page.reloadPage();
			});
		}

		if (forceReloadActionOriginal) {
			const isRefreshPopoverHidden = this.dashboardService.isRefreshPopoverHidden();

			addAction(
				forceReloadActionOriginal,
				async () => {
					this.dataModelCacheService.resetCache();
					this.page.reloadPage();
				},
				isRefreshPopoverHidden ? undefined : this.i18n.getFormatted('shell.dashboards.cacheHint', this.configService.get('dataSourcesCacheLifetime')),
				isRefreshPopoverHidden ? undefined : () => this.dashboardService.setRefreshPopoverHidden()
			);
		}

		const editAction = this.createAction({
			name: 'UUX.Client.Dashboard.Edit',
			title: this.i18n.get('solutionBuilderAgent.common.edit'),
			icon: 'edit',
			execute: () => {
				this.page.enableConfigurationMode();
				this.dataModelCacheService.disable();
			},
		});
		dashboardActions.push(editAction);

		return dashboardActions;
	}

	private createAction({
		name,
		title,
		icon,
		execute,
		popover,
		onPopoverClose,
	}: {
		name: string;
		title: string;
		icon: string;
		popover?: string;
		execute: () => void;
		onPopoverClose?: () => void;
	}) {
		const action = new MxAction(name, title, icon);
		action.popover = popover;
		if (onPopoverClose) {
			action.onPopoverClose = onPopoverClose;
		}
		action.controller = function () {
			return {
				execute,
			};
		};
		return action;
	}
}

export const DashboardControlPanelComponent = {
	name: 'mxDashboardControlPanel',
	controller: DashboardControlPanelController,
	controllerAs: 'vm',
	template,
	require: {
		page: `^${DashboardPageComponent.name}`,
	},
};
